// src/stores/parlayStore.ts
import { create } from 'zustand';
import { BasePick, Parlay, ParlayStatus } from '../domain/types';
import { buildParlay, validateParlay } from '../services/parlayEngine';
import { createParlayAndTrack, addPickToTracker } from '../useCases/parlayUseCases';
import { indexedDBRepo } from '../services/trackerRepo';

interface ParlayBuilderState {
  // pernas selecionadas
  legs: BasePick[];
  stake: number;

  // ações builder
  addLeg: (pick: BasePick) => void;
  removeLeg: (pickId: string) => void;
  clearLegs: () => void;
  setStake: (stake: number) => void;

  // validação
  canBuild: boolean;
  validationError: string | null;

  // parlay salvos (do tracker)
  savedParlays: Parlay[];
  isLoadingSaved: boolean;

  loadSaved: () => Promise<void>;
  saveParlay: () => Promise<Parlay>;
  saveSinglePick: (pick: BasePick & { stake: number }) => Promise<void>;

  // resolução
  resolveAll: (fetchStat: (pick: BasePick) => Promise<number>) => Promise<void>;
}

export const useParlayStore = create<ParlayBuilderState>((set, get) => ({
  legs: [],
  stake: 10,

  addLeg: (pick) => {
    if (get().legs.some(l => l.id === pick.id)) return;
    const newLegs = [...get().legs, pick];
    const validation = validateParlay(newLegs);
    set({
      legs: newLegs,
      canBuild: validation.ok,
      validationError: validation.error ?? null,
    });
  },

  removeLeg: (pickId) => {
    const newLegs = get().legs.filter(l => l.id !== pickId);
    const validation = validateParlay(newLegs);
    set({
      legs: newLegs,
      canBuild: validation.ok && newLegs.length >= 2,
      validationError: validation.error ?? null,
    });
  },

  clearLegs: () => set({ legs: [], canBuild: false, validationError: null }),
  setStake: (stake) => set({ stake }),

  canBuild: false,
  validationError: null,

  savedParlays: [],
  isLoadingSaved: false,

  loadSaved: async () => {
    set({ isLoadingSaved: true });
    const entries = await indexedDBRepo.getAll();
    const parlays = entries
      .filter(e => e.type === 'parlay')
      .map(e => e.data as Parlay)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    set({ savedParlays: parlays, isLoadingSaved: false });
  },

  saveParlay: async () => {
    const { legs, stake } = get();
    const validation = validateParlay(legs);
    if (!validation.ok) throw new Error(validation.error);
    const parlay = await createParlayAndTrack(legs, stake, indexedDBRepo);
    set(state => ({
      savedParlays: [parlay, ...state.savedParlays],
      legs: [],
      canBuild: false,
    }));
    return parlay;
  },

  saveSinglePick: async (pick) => {
    await addPickToTracker(pick, indexedDBRepo);
  },

  resolveAll: async (fetchStat) => {
    const { resolvePending } = await import('../useCases/parlayUseCases');
    await resolvePending(indexedDBRepo, fetchStat);
    // recarrega
    get().loadSaved();
  },
}));